import React from 'react';
// components
import Link from 'components/Link';
import BadgeColumn from '../BadgeColumn';
import Badge from 'components/Badge';
import Tooltip from 'components/Tooltip';
import NoteColumn from '../NoteColumn';
import { FormattedMessage } from 'react-intl';
// utils
import { formatDate } from 'utils/formatDateHelper';
import { buildProdUrl } from 'utils/redirectHelper';
// types
import { FormatMessageFunc, SalesResultWithIncluded, RendererType, Column } from 'types';
// renderers
import { renderTooltipColumn, renderUserAvatarColumn } from '../columnRenderers';
// styles
import styles from '../Columns.module.scss';

const renderDeliveredDateTime: RendererType<SalesResultWithIncluded> = (
  { delivered_at },
  { emptyPlaceholder }
) => (
  <BadgeColumn
    data={delivered_at ? formatDate(delivered_at, 'MM/dd/yy • h:mm a') : ''}
    placeholder={emptyPlaceholder}
  />
);

const renderLogisticsLoadNumber: RendererType<SalesResultWithIncluded> = ({
  loading: { id, name },
  is_duplicate,
}) => (
  <Link to={`/scheduling/loads/${id}`} className={styles['results-load-name']}>
    {name}
    {is_duplicate && (
      <Tooltip
        size="small"
        trigger={<Badge className="ml-5" label="D" />}
        tooltipContent={<FormattedMessage id="general.duplicate" />}
      />
    )}
  </Link>
);

const renderTruckingLoadNumber: RendererType<SalesResultWithIncluded> = ({
  loading: { id, name },
}) => (
  <Link
    to={`/trucking/archive/${id}/sales-results?from=/trucking/sales-results`}
    className={styles['results-load-name']}
  >
    {name}
  </Link>
);

export const truckingResultsColumns = [
  'loadNameTrucking',
  'targetHead',
  'headCount',
  'headDelivered',
  'avgPigWeight',
  'prelimWeight',
  'coefficientOfVariation',
  'deadOnArrival',
  'plantName',
  'contractName',
  'originName',
  'deliveryDateTime',
  'status',
  'user',
];

export const getSalesResultsColumns = (
  formatMessage: FormatMessageFunc
): Record<string, Column<SalesResultWithIncluded>> => ({
  loadName: {
    label: formatMessage({ id: 'loadsTable.loadNumber' }),
    renderer: renderLogisticsLoadNumber,
    width: 120,
    sortable: 'loading_name',
  },
  loadNameTrucking: {
    label: formatMessage({ id: 'loadsTable.loadNumber' }),
    renderer: renderTruckingLoadNumber,
    width: 200,
    sortable: 'loading_name',
  },
  targetHead: {
    label: formatMessage({ id: 'general.targetHead' }),
    dataPath: 'destination_loading_commitment.contract_commitment.head_count',
    rendererType: 'badge',
    sortable: 'contract_commitment_head_count',
    width: 100,
  },
  headCount: {
    label: formatMessage({ id: 'general.headScheduled' }),
    dataPath: 'destination_loading_commitment.head_count',
    rendererType: 'badge',
    sortable: 'destination_loading_commitment_head_count',
    width: 100,
  },
  headDelivered: {
    label: formatMessage({ id: 'loadsTable.prelimHeadDelivered' }),
    width: 100,
    sortable: 'head_count',
    renderer: (record: SalesResultWithIncluded, { emptyPlaceholder }) => {
      const value = record.head_count?.toString() ?? '';
      const content = record.head_count_changed_flag ? (
        <span style={{ color: 'red' }}>{value}</span>
      ) : (
        value
      );
      return <BadgeColumn data={content} placeholder={emptyPlaceholder} />;
    },
  },
  avgPigWeight: {
    label: formatMessage({ id: 'load.avgWeight' }),
    dataPath: 'avg_pig_weight',
    rendererType: 'weight',
    sortable: 'avg_pig_weight',
    width: 80,
    renderer: (record: SalesResultWithIncluded, { emptyPlaceholder }) => {
      const value = record.avg_pig_weight?.toString() ?? '';
      const content = record.avg_pig_weight_changed_flag ? (
        <span style={{ color: 'red' }}>{value}</span>
      ) : (
        value
      );
      return <BadgeColumn data={content} placeholder={emptyPlaceholder} />;
    },
  },
  prelimWeight: {
    label: formatMessage({ id: 'loadsTable.prelimWeight' }),
    dataPath: 'total_weight',
    rendererType: 'weight',
    sortable: 'total_weight',
    width: 100,
  },
  cutLevel: {
    label: formatMessage({ id: 'loadsTable.cutLevel' }),
    renderer: ({ source_loading_commitment }) => (
      <BadgeColumn
        data={
          source_loading_commitment?.cut_level
            ? formatMessage({ id: `cutLevel.${source_loading_commitment?.cut_level}` })
            : ''
        }
        placeholder="N/A"
      />
    ),
    sortable: 'source_loading_commitment_cut_level',
    width: 90,
  },
  coefficientOfVariation: {
    label: formatMessage({ id: 'loadsTable.coefficientOfVariation' }),
    emptyPlaceholder: '0',
    dataPath: 'coefficient_of_variation',
    rendererType: 'badge',
    width: 60,
    sortable: 'coefficient_of_variation',
  },
  deadOnArrival: {
    label: formatMessage({ id: 'loadsTable.deadOnArrival' }),
    emptyPlaceholder: '0',
    dataPath: 'dead_on_arrival',
    rendererType: 'badge',
    width: 60,
    sortable: 'dead_on_arrival',
    renderer: (record: SalesResultWithIncluded, { emptyPlaceholder }) => {
      const value = record.dead_on_arrival?.toString() ?? '';
      const content = record.dead_on_arrival_changed_flag ? (
        <span style={{ color: 'red' }}>{value}</span>
      ) : (
        value
      );
      return <BadgeColumn data={content} placeholder={emptyPlaceholder} />;
    },
  },
  plantName: {
    label: formatMessage({ id: 'general.plant' }),
    renderer: ({ destination_loading_commitment }, data) =>
      renderTooltipColumn(destination_loading_commitment?.packing_plant?.name || '', {
        ...data,
        valueMaxWidth: 160,
      }),
    sortable: 'packing_plant_name',
    width: 160,
  },
  contractName: {
    label: formatMessage({ id: 'general.contract' }),
    renderer: ({ destination_loading_commitment }, data) =>
      renderTooltipColumn(
        destination_loading_commitment?.contract_commitment?.contract?.name || '',
        {
          ...data,
          valueMaxWidth: 160,
        }
      ),
    sortable: 'contract_name',
    width: 160,
  },
  originName: {
    label: formatMessage({ id: 'loadsTable.origin' }),
    renderer: ({ source_loading_commitment }, data) =>
      renderTooltipColumn(source_loading_commitment?.farm?.name || '', {
        ...data,
        valueMaxWidth: 160,
      }),
    sortable: 'farm_name',
    width: 160,
  },
  originGroup: {
    label: formatMessage({ id: 'loadsTable.originGroup' }),
    renderer: ({ source_loading_commitment }, data) => {
      const group = source_loading_commitment?.pig_group;
      const groupName = group?.name || '';
      const groupId = group?.id;
      const href = groupId ? buildProdUrl(`/groups/${groupId}/scheduled-loads`) : '';

      return renderTooltipColumn(
        href ? (
          <a
            href={href}
            onClick={e => e.stopPropagation()}
            target="_blank"
            rel="noopener noreferrer"
            className={styles['results-load-name']}
          >
            {groupName}
          </a>
        ) : (
          groupName
        ),
        {
          ...data,
          valueMaxWidth: 180,
        }
      );
    },
    sortable: 'pig_group_name',
    width: 120,
  },
  source: {
    label: formatMessage({ id: 'general.source' }),
    renderer: ({ source_loading_commitment }, data) =>
      renderTooltipColumn(source_loading_commitment?.pig_group?.external_source_name || '', {
        ...data,
        valueMaxWidth: 160,
      }),
    sortable: 'pig_group_external_source_name',
    width: 160,
  },
  truckingName: {
    label: formatMessage({ id: 'loadsTable.trucking' }),
    renderer: ({ loading }, data) =>
      renderTooltipColumn(loading?.trucking_company?.name || '', {
        ...data,
        valueMaxWidth: 160,
      }),
    sortable: 'trucking_company_name',
    width: 160,
  },
  deliveryDateTime: {
    label: formatMessage({ id: 'general.deliveryDate' }),
    renderer: renderDeliveredDateTime,
    sortable: 'delivered_at',
    width: 135,
  },
  status: {
    label: formatMessage({ id: 'general.status' }),
    rendererType: 'badgeStatus',
    dataPath: 'loading.status',
    width: 100,
    sortable: 'loading_status',
  },
  dataSource: {
    label: formatMessage({ id: 'loadsTable.dataSource' }),
    dataPath: 'data_source',
    width: 135,
    sortable: 'data_source',
  },
  originNotes: {
    label: formatMessage({ id: 'general.originNotes' }),
    width: 200,
    sortable: 'source_loading_commitment_comment',
    renderer: ({ source_loading_commitment }, { emptyPlaceholder, options }) => (
      <NoteColumn
        maxWidth={options?.maxWidth}
        note={source_loading_commitment?.comment || ''}
        placeholder={emptyPlaceholder}
      />
    ),
    options: { maxWidth: 180 },
  },
  destinationNotes: {
    label: formatMessage({ id: 'general.destinationNotes' }),
    width: 200,
    sortable: 'destination_loading_commitment_comment',
    renderer: ({ destination_loading_commitment }, { emptyPlaceholder, options }) => (
      <NoteColumn
        maxWidth={options?.maxWidth}
        note={destination_loading_commitment?.comment || ''}
        placeholder={emptyPlaceholder}
      />
    ),
    options: { maxWidth: 180 },
  },
  internalNote: {
    label: formatMessage({ id: 'general.internalNote' }),
    width: 200,
    sortable: 'loading_internal_comment',
    renderer: ({ loading }, { emptyPlaceholder, options }) => (
      <NoteColumn
        maxWidth={options?.maxWidth}
        note={loading?.internal_comment || ''}
        placeholder={emptyPlaceholder}
      />
    ),
    options: { maxWidth: 180 },
  },
  user: {
    label: '',
    renderer: ({ modified_by }) => renderUserAvatarColumn(modified_by),
    emptyPlaceholder: '',
    width: 40,
  },
});
